import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  CellContext,
  ColumnDef,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Database, KeyDeal, NestedDealStalled, Scope, TODO } from "@revelate/types";
import { getCurrencyFormatted } from "@/lib/app";
import { useEffect, useState } from "react";
import { getFullName } from "@revelate/utils";
import { getDealOwner } from "@revelate/utils";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  formatRiskPoints,
  mapRiskName,
  type RiskIndicator,
} from "@/revenue-intelligence/lib/risk-contracts";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
// import { Progress } from "@/components/ui/progress";

type Currency = Database["public"]["Tables"]["currencies"]["Row"] | null;

type AtRiskDealColumnsOpts = {
  showTopRisk?: boolean,
  showConcentration?: boolean;
  highlightIndicator?: RiskIndicator,
  hideRiskColumn?: boolean;
  showExpectedCloseDate?: boolean;
  showForecastImpact?: boolean;
  showForecastShare?: boolean;
  showUrgency?: boolean;
  showUrgencyBadge?: boolean;
  forecastValueDisplay?: "contribution" | "impact";
  topRiskDisplay?: "top-risk" | "primary-reason";
  valueHeader?: "value" | "exposed-value";
  displayMode?: "default" | "key-forecast-deals" | "recent-loss-signals";
  companyCurrency?: Currency;
};

const defaultAtRiskDealColumnOpts: AtRiskDealColumnsOpts = {
  showTopRisk: false,
  showConcentration: false,
  highlightIndicator: undefined,
  hideRiskColumn: false,
  showExpectedCloseDate: false,
  showForecastImpact: false,
  showForecastShare: false,
  showUrgency: false,
  showUrgencyBadge: false,
  forecastValueDisplay: "impact",
  topRiskDisplay: "top-risk",
  valueHeader: "value",
  displayMode: "default",
  companyCurrency: null,
};

export function getAtRiskDealsColumns(opts: AtRiskDealColumnsOpts): ColumnDef<TODO, TODO>[] {
  const columnOpts = {
    ...defaultAtRiskDealColumnOpts,
    ...opts,
  };
  const isKeyForecastDeals = columnOpts.displayMode === "key-forecast-deals";
  const isRecentLossSignals = columnOpts.displayMode === "recent-loss-signals";

  if (isRecentLossSignals) {
    return [
      {
        id: "name",
        header: "Deal",
        cell: ({ row }) => (
          <div className="font-medium group-hover:underline">
            {row.original.name}
          </div>
        ),
      },
      {
        id: "owner",
        header: "Owner",
        cell: ({ row }) => (
          <span className="block max-w-[150px] truncate">
            {getDealOwner(row.original)
              ? getFullName(getDealOwner(row.original))
              : "No owner"}
          </span>
        ),
      },
      {
        id: "loss_signals",
        header: "Signals before loss",
        cell: ({ row }) => {
          const signals = Array.isArray(row.original.lossSignalIndicators)
            ? row.original.lossSignalIndicators as Array<{ indicator: RiskIndicator; score: number }>
            : [];
          if (!signals.length) return <span className="text-gray-400">—</span>;

          return (
            <div className="space-y-1">
              {signals.map(signal => (
                <div key={signal.indicator} className="flex items-center justify-between gap-3">
                  <span>{mapRiskName(signal.indicator)}</span>
                  <span className="shrink-0 tabular-nums text-gray-500">
                    {formatRiskPoints(signal.score)}
                  </span>
                </div>
              ))}
            </div>
          );
        },
      },
      {
        id: "closed_lost",
        header: "Closed lost",
        cell: ({ row }) => {
          if (!row.original.closed_at) return <span className="text-gray-400">—</span>;
          const closedAt = new Date(row.original.closed_at);
          if (Number.isNaN(closedAt.getTime())) return <span className="text-gray-400">—</span>;
          return <span className="tabular-nums">{format(closedAt, "MMM d, yyyy")}</span>;
        },
      },
    ];
  }
  const expectedCloseColumn: ColumnDef<TODO, TODO> = {
    id: "expected_close",
    header: "Expected close",
    cell: ({ row }: CellContext<TODO, TODO>) => {
      const expectedCloseDate = row.original.adjustedCloseDate;
      if (!expectedCloseDate) return <span className="text-gray-400">—</span>;

      const parsedDate = new Date(expectedCloseDate);
      if (Number.isNaN(parsedDate.getTime())) {
        return <span className="text-gray-400">—</span>;
      }

      return <span className="tabular-nums">{format(parsedDate, "MMM d, yyyy")}</span>;
    },
  };

  const columns: ColumnDef<TODO, TODO>[] = [
    {
      id: "name",
      header: "Deal",
      cell: ({ row }) => {
        const urgency = row.original.urgency_bucket as "high" | "medium" | "low" | undefined;
        return (
          <div className="flex items-center gap-2 font-medium">
            <span className="group-hover:underline">{row.original.name}</span>
            {columnOpts.showUrgencyBadge && urgency && (
              <Badge
                variant="outline"
                className="shrink-0 border-[#E0B1CB] bg-[#E0B1CB]/10 text-[10px] font-medium text-[#5E548E]"
                aria-label={`Urgent deal with ${urgency} relative urgency`}
                title={`60-day Urgency shortlist · ${urgency[0]?.toUpperCase()}${urgency.slice(1)} relative urgency`}
              >
                Urgent
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        return (
          <div className="flex space-x-2">
            <span className="max-w-[150px] truncate">
              {getDealOwner(row.original)
                ? getFullName(getDealOwner(row.original))
                : "No owner"}
            </span>
          </div>
        );
      },
    },
    {
      header: isKeyForecastDeals
        ? "Deal value"
        : columnOpts.valueHeader === "exposed-value" ? "Exposed value" : "Value",
      cell: ({ row }) => {
        return (
          <div className="min-w-28 shrink-0 tabular-nums">
            {isKeyForecastDeals
              ? formatCompactIsoCurrency(Number(row.original.value ?? 0), columnOpts.companyCurrency ?? null)
              : `${new Intl.NumberFormat("sv-SE").format(row.original.value)} ${row.original.currency_code}`}
          </div>
        );
      }
    },
    ...(columnOpts.showForecastImpact
      ? [{
        id: "forecast_impact",
        header: isKeyForecastDeals
          ? "Forecast value"
          : columnOpts.forecastValueDisplay === "contribution"
          ? "Forecast contribution"
          : "Forecast impact",
        cell: ({ row }: CellContext<TODO, TODO>) => {
          const forecastImpact = Number(row.original.forecast_contribution ?? 0);
          return (
            <div className="min-w-28 shrink-0 tabular-nums">
              {isKeyForecastDeals
                ? formatCompactIsoCurrency(forecastImpact, columnOpts.companyCurrency ?? null)
                : `${new Intl.NumberFormat("sv-SE").format(forecastImpact)} ${row.original.currency_code}`}
            </div>
          );
        },
      }]
      : []),
    ...(columnOpts.showForecastShare
      ? [{
        id: "forecast_share",
        header: "Share of Forecast",
        cell: ({ row }: CellContext<TODO, TODO>) => {
          const forecastShare = Number(row.original.forecast_share);
          if (!Number.isFinite(forecastShare)) {
            return <span className="text-gray-400">—</span>;
          }

          return (
            <span className="tabular-nums">
              {new Intl.NumberFormat("en", {
                maximumFractionDigits: 1,
                style: "percent",
              }).format(forecastShare)}
            </span>
          );
        },
      }]
      : []),
    ...(columnOpts.showExpectedCloseDate && !isKeyForecastDeals
      ? [expectedCloseColumn]
      : []),
    {
      header: "Stage",
      accessorFn: (row) => row.stage_label ?? row.provider_data.properties.dealstage ?? "",
    },
    ...(columnOpts.showExpectedCloseDate && isKeyForecastDeals
      ? [expectedCloseColumn]
      : []),
  ];

  if (!columnOpts.hideRiskColumn) {
    columns.push({
      id: "weighted_risk_score",
      header: "Risk",
      cell: ({ row }) => {
        const riskBucket = row.original.weighted_risk_score >= 50
          ? "high"
          : row.original.weighted_risk_score >= 25
            ? "medium"
            : "low";
        const badgeVariants = {
          high: "rejected",
          medium: "approved",
          low: "paid",
        } satisfies Record<typeof riskBucket, BadgeProps["variant"]>;

        return (
          <Badge
            variant={badgeVariants[riskBucket]}
            className="capitalize"
          >
            {riskBucket}
          </Badge>
        );
      },
    });
  }

  if (columnOpts.showUrgency) {
    columns.push({
      id: "urgency",
      header: "Urgency",
      cell: ({ row }) => {
        const urgency = row.original.urgency_bucket as "high" | "medium" | "low" | undefined;
        if (!urgency) return <span className="text-gray-400">—</span>;
        const badgeVariants = {
          high: "rejected",
          medium: "approved",
          low: "paid",
        } satisfies Record<NonNullable<typeof urgency>, BadgeProps["variant"]>;

        return (
          <Badge variant={badgeVariants[urgency]} className="capitalize">
            {urgency}
          </Badge>
        );
      },
    });
  }

  if (columnOpts.showTopRisk) {
    columns.push({
      id: "topRisk",
      header: columnOpts.topRiskDisplay === "primary-reason" ? "Primary reason" : "Top Risk",
      cell: ({ row }) => {
        const topRisk = row.original?.topRisk;
        if (!topRisk?.indicator) return <span className="text-gray-400">—</span>;

        return (
          <div>
            <p>{mapRiskName(topRisk.indicator as RiskIndicator)}</p>
          </div>
        );
      },
    })
  }

  if (columnOpts.highlightIndicator) {
    columns.push({
      id: "highlightedRisk",
      header: `${mapRiskName(columnOpts.highlightIndicator)} Risk Score`,
      cell: ({ row }) => {
        const highlightedRisk =
          row.original?.risks[columnOpts.highlightIndicator as string]?.risk ?? 0;

        return (
          <div>
            <p>{formatRiskPoints(highlightedRisk)}</p>
          </div>
        );
      },
    })
  }

  if (columnOpts.showConcentration) {
    columns.push({
      id: "concentration",
      header: "% of Pipeline",
      cell: ({ row }) => {
        const concentrationPct = row.original.concentrationPct ?? 0;

        return (
          <p>{`${concentrationPct.toFixed(1)}%`}</p>
        );
      },
    })
  }

  return columns;
}

export const getKeyDealsColumns = (
  companyCurrencyCode: string,
  showCommissionColumn = true
): ColumnDef<KeyDeal, TODO>[] => {
  const columns: ColumnDef<KeyDeal, TODO>[] = [
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        return (
          <div className="flex space-x-2">
            <span className="max-w-[150px] truncate font-medium">
              {row.original.owner ? getFullName(row.original.owner) : "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "dealName",
      header: "Deal",
    },
    {
      header: "Deal value",
      accessorFn: (row) =>
        getCurrencyFormatted(row.dealValue, row.dealCurrencyCode),
    },
  ];

  if (showCommissionColumn) {
    columns.push({
      header: "Commission",
      accessorFn: (row) =>
        getCurrencyFormatted(row.commissionValue, companyCurrencyCode),
    });
  }

  return columns;
};

export const getStalledDealsColumns = (): ColumnDef<
  NestedDealStalled,
  TODO
>[] => [
    {
      id: "owner",
      header: "Owner",
      cell: ({ row }) => {
        return (
          <div className="flex space-x-2">
            <span className="max-w-[150px] truncate font-medium">
              {getDealOwner(row.original)
                ? getFullName(getDealOwner(row.original))
                : "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Deal",
    },
    {
      header: "Deal value",
      accessorFn: (row) => getCurrencyFormatted(row.value, row.currency_code),
    },
    {
      accessorKey: "comment",
      header: "Reason",
    },
  ];

interface KeyDealsDataTableProps<TData, TValue> {
  keyDeals: KeyDeal[];
  columns: ColumnDef<TData, TValue>[];
  scope: Scope | null;
}

export function KeyDealsDataTable({
  keyDeals,
  columns,
  scope,
}: KeyDealsDataTableProps<KeyDeal, TODO>) {
  const [columnVisibility, setColumnVisibility] = useState({
    owner: true,
  });

  useEffect(() => {
    if (scope?.type === "user") {
      setColumnVisibility({ owner: false });
    } else {
      setColumnVisibility({ owner: true });
    }
  }, [scope]);

  const table = useReactTable({
    data: keyDeals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility },
  });

  return (
    <div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead className="px-0 pr-5" key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell className="px-0 pr-5" key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface NestedDealsMiniDataTableProps<TData, TValue> {
  deals: NestedDealStalled[];
  columns: ColumnDef<TData, TValue>[];
  scope: Scope | null;
}

export function NestedDealsMiniDataTable({
  deals,
  columns,
  scope,
}: NestedDealsMiniDataTableProps<NestedDealStalled, TODO>) {
  const [columnVisibility, setColumnVisibility] = useState({
    owner: true,
  });

  useEffect(() => {
    if (scope?.type === "user") {
      setColumnVisibility({ owner: false });
    } else {
      setColumnVisibility({ owner: true });
    }
  }, [scope]);

  const table = useReactTable({
    data: deals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility },
  });

  return (
    <div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead className="px-0 pr-5" key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell, index) => (
                <TableCell className="px-0 pr-5" key={cell.id}>
                  {cell.column.id === "comment" && index === 2 ? (
                    <Badge variant="unapproved">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Badge>
                  ) : (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
